import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { getInsightFormatOrDefault } from 'libs/end-to-end/src/insights';
import { getInsightWithReferenceFormatOrDefault } from 'libs/end-to-end/src/insights';
import { Insight } from 'libs/end-to-end/src/insights';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { usePagination } from 'apps/sigma2-ui/src/lib/hooks/usePagination';
import { PaginationButtons } from 'apps/sigma2-ui/src/components/ui/PaginationButtons';
import { IconKeyTakeawayAchievementSvg } from 'apps/sigma2-ui/src/components/svg/IconKeyTakeawayAchievement';
import { FocusOnIconSvg } from 'apps/sigma2-ui/src/components/svg/FocusOnIcon';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { getInsightsType } from 'apps/sigma2-ui/src/models/user';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

// TODO: Add in the callout for pattern detected, which can be found in the history of this file.

interface CarouselCardStackProps<Item extends object> {
  items: Item[];
  bodyDefinition: (
    item: Item,
    config: {
      updateCardStyle: (style: React.CSSProperties) => void;
    },
  ) => React.ReactNode;
  footerDefinition?: (item: Item) => React.ReactNode;
  overrides?: {
    cardClassName?: string;
  };
}

export function CarouselCardStack<Item extends object>({
  items,
  bodyDefinition,
  footerDefinition,
  overrides,
}: CarouselCardStackProps<Item>): React.ReactElement {
  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({});
  const totalPages = items.length;

  const { currentPage, onPreviousPageClick, onNextPageClick } = usePagination({
    totalPages,
  });

  const currentPageIndex = currentPage - 1;
  const currentItem = items[currentPageIndex];

  const updateCardStyle = useCallback((style: React.CSSProperties) => {
    setCardStyle(style);
  }, []);

  return (
    <div
      className={cn(
        'bg-white rounded-lg shadow-2px p-6',
        !currentItem && 'invisible',
        overrides?.cardClassName,
      )}
      style={cardStyle}
    >
      {currentItem && (
        <>
          {bodyDefinition(currentItem, { updateCardStyle })}

          {totalPages > 1 && (
            <div className="absolute bottom-4 left-5">
              <PaginationButtons
                totalPages={totalPages}
                currentPage={currentPage}
                onPreviousPageClick={onPreviousPageClick}
                onNextPageClick={onNextPageClick}
              />
            </div>
          )}

          {footerDefinition && footerDefinition(currentItem)}
        </>
      )}
    </div>
  );
}

interface KeyTakeawayCardStackItem {
  insight: Insight;
  focusArea: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
}

interface KeyTakeawayCardStackProps {
  variant?: 'achievements' | 'risks';
  items: KeyTakeawayCardStackItem[];
  calibrationFraming: CalibrationFraming;
}

const iconMap: Record<'achievements' | 'risks', React.ReactNode> = {
  achievements: <IconKeyTakeawayAchievementSvg />,
  risks: <FocusOnIconSvg svg={{ className: 'h-8 w-8 text-raspberry-600' }} />,
};

export const KeyTakeawayCardStack: React.FC<KeyTakeawayCardStackProps> = ({
  variant,
  items,
  calibrationFraming,
}) => {
  const language = useLanguage();

  const { getDimensionByColumnHeaderValue, getLevelByColumnValue } =
    useDimensionConfig();

  if (!items.length) {
    return <></>;
  }

  return (
    <CarouselCardStack
      overrides={{ cardClassName: 'flex-1 space-y-5 relative min-h-[18rem]' }}
      items={items}
      bodyDefinition={(item) => {
        const { insight, focusArea, metricDefinition } = item;

        const dimensionDefinition = getDimensionByColumnHeaderValue(
          insight.dimension,
        );

        const referenceDefinition = getLevelByColumnValue(
          insight.dimension,
          insight.referenceLevel,
        );
        const treatmentDefinition = getLevelByColumnValue(
          insight.dimension,
          insight.treatmentLevel,
        );

        const empsOrAppsDisplay = metricDefinition.whiteLabeling.subjectPlural;

        const insightFormat = getInsightFormatOrDefault(
          dimensionDefinition?.insightFormat,
        );

        const showBeforeEmpsOrApps =
          dimensionDefinition?.showBeforeEmpsOrApps ?? false;

        const referenceDisplay = referenceDefinition?.displayName ?? '';

        const insightWithReferenceDisplay =
          language.insightWithReferenceDisplay({
            referenceDisplay,
            showBeforeEmpsOrApps,
            empsOrAppsDisplay,
            insightWithReferenceFormat: getInsightWithReferenceFormatOrDefault(
              dimensionDefinition?.insightWithReferenceFormat,
            ),
            levelDisplay: treatmentDefinition?.displayName ?? '',
          });

        const insightDisplay = language.insightDisplay({
          showBeforeEmpsOrApps,
          empsOrAppsDisplay,
          insightFormat,
          levelDisplay: treatmentDefinition?.displayName ?? '',
        });

        const activeVariant = variant
          ? variant
          : getInsightsType({ metricDefinition, insight });

        const icon = iconMap[activeVariant];

        const titleMap: Record<'achievements' | 'risks', TranslatedContent> = {
          achievements: language.focusAreaKeyTakeawayCardAchievementTitle(),
          risks: language.focusAreaKeyTakeawayCardRiskTitle(),
        };

        const title = titleMap[activeVariant];

        const levelDisplay = treatmentDefinition?.displayName ?? '';

        return (
          <KeyTakeawayCard
            title={title}
            focusArea={focusArea}
            icon={icon}
            insight={insight}
            levelDisplay={levelDisplay}
            language={language}
            insightWithReferenceDisplay={insightWithReferenceDisplay}
            insightDisplay={insightDisplay}
            calibrationFraming={calibrationFraming}
          />
        );
      }}
    />
  );
};

const KeyTakeawayCard = ({
  title,
  focusArea,
  icon,
  insight,
  levelDisplay,
  language,
  insightWithReferenceDisplay,
  insightDisplay,
  calibrationFraming,
}: {
  title: TranslatedContent;
  focusArea: MyFocusAreaDefinition;
  icon: React.ReactNode;
  insight: Insight;
  levelDisplay: string;
  language: Phrases;
  insightWithReferenceDisplay: string;
  insightDisplay: string;
  calibrationFraming: CalibrationFraming;
}) => {
  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <S2Typography variant={'headline-5-alt'} translatedContent={title} />

        <div className="text-secondary">{icon}</div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-row justify-stretch">
          <div className="space-y-1 flex-1">
            <S2Typography
              variant="headline-6-alt"
              translatedContent={language.insightType({
                insight,
                levelDisplay: levelDisplay,
              })}
            />

            <S2Typography
              variant="body-7"
              translatedContent={language.insightTextNew({
                phrases: language,
                insight,
                insightWithReferenceDisplay,
                insightDisplay,
                focusAreaDisplay: focusArea.displayName,
                lowerHigherDisplay: language.lowerHigherDisplay({
                  type: insight?.highlight?.type ?? '',
                }),
                calibrationFraming,
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
